import { Button, Loader } from '@southfields-digital/mxmz-react-ui';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { validate as isValidUUID } from 'uuid';

import { getRepeaterItems, transformSportDataValue } from 'src/components/GraphicPropertiesForm';
import Repeater from 'src/components/Repeater';
import { StateType } from 'src/redux/reducers';
import { REQUEST_GET_SPORT_DATA_HOCKEY_STANDINGS } from 'src/redux/reducers/sportData';

import SportDataSelect from '../SportDataSelect';

import styles from './HockeyStandings.module.scss';

type Props = {
  handleImportData: (values: PropertyValue[]) => void;
  selectedProperty: RepeaterProperties;
};

const MATCH_OPTIONS = [
  { label: 'Select match..', value: '' },

  // WOMEN
  { label: 'W01: Railway Union vs Watsonians (Final 12)', value: '5511' },
  { label: 'W02: Slavia Prague vs Gantoise (Final 12)', value: '5512' },
  { label: 'W03: Reading vs Mannheimer (Final 12)', value: '5513' },
  { label: 'W04: SCHC vs Egara (Final 12)', value: '5514' },
  { label: 'W05: Loser 01 vs Loser 02 (Ranking Match)', value: '5515' },
  { label: 'W06: Winner 02 vs Campo Madrid (Final 8)', value: '5516' },
  { label: 'W07: Den Bosch vs Winner 01 (Final 8)', value: '5517' },
  { label: 'W08: Loser 03 vs Loser 04 (Ranking Match)', value: '5518' },
  { label: 'W09: Harvestehuder vs Winner 03 (Final 8)', value: '5519' },
  { label: 'W10: Winner 04 vs Braxgata (Final 8)', value: '5520' },
  { label: 'W11: Loser 06 vs Loser 07 (Ranking Match)', value: '5521' },
  { label: 'W12: Winner 06 vs Winner 07 (Final 4)', value: '5522' },
  { label: 'W13: Winner 09 vs Winner 10 (Final 4)', value: '5523' },
  { label: 'W14: Loser 09 vs Loser 10 (Ranking Match)', value: '5524' },
  { label: 'W15: Loser 12 vs Loser 13 (Bronze Match)', value: '5525' },
  { label: 'W16: Winner 12 vs Winner 13 (Grand Final)', value: '5526' },

  // MEN
  { label: 'M01: Old Georgians vs Gantoise (Final 8)', value: '5527' },
  { label: 'M02: Surbiton vs Campo Madrid (Final 8)', value: '5528' },
  { label: 'M03: Amsterdam vs Hamburger Polo (Final 8)', value: '5529' },
  { label: 'M04: Kampong vs Crefelder (Final 8)', value: '5530' },
  { label: 'M05: Loser 02 vs Loser 01 (Ranking Match)', value: '5531' },
  { label: 'M06: Winner 02 vs Winner 01 (Final 4)', value: '5532' },
  { label: 'M07: Winner 03 vs Winner 04 (Final 4)', value: '5533' },
  { label: 'M08: Loser 03 vs Loser 04 (Ranking Match)', value: '5534' },
  { label: 'M09: Loser 06 vs Loser 07 (Bronze Match)', value: '5535' },
  { label: 'M10: Winner 06 vs Winner 07 (Grand Final)', value: '5536' },
];

export default function HockeyStandings({ handleImportData, selectedProperty }: Props) {
  const dispatch = useDispatch();
  const { hockeyStandings } = useSelector((state: StateType) => state.sportData);

  const [matchId, setMatchId] = useState<string>(hockeyStandings.data.matchId || '');

  const propertyValues = useMemo<PropertyValue[]>(
    () =>
      hockeyStandings.data.items
        ?.map(({ statsType, statsValueHome, statsValueAway }) => ({
          statsType,
          statsValueHome,
          statsValueAway,
        }))
        .reverse() || [],
    [hockeyStandings.data.items]
  );

  const transformedPropertyValues = useMemo(() => {
    if (!propertyValues.length) return propertyValues;
    return isValidUUID(selectedProperty.id)
      ? (transformSportDataValue(propertyValues, selectedProperty) as PropertyValue[])
      : propertyValues;
  }, [propertyValues, selectedProperty]);

  const repeaterValues = useMemo(
    () =>
      transformedPropertyValues.length
        ? getRepeaterItems(selectedProperty, transformedPropertyValues)
        : [],
    [transformedPropertyValues, selectedProperty]
  );

  useEffect(() => {
    if (!matchId) return;
    dispatch({
      type: REQUEST_GET_SPORT_DATA_HOCKEY_STANDINGS,
      payload: { matchId },
    });
  }, [matchId, dispatch]);

  const handleChangeMatch = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setMatchId(e.currentTarget.value);
  }, []);

  const handleClickImport = useCallback(() => {
    handleImportData(transformedPropertyValues);
  }, [handleImportData, transformedPropertyValues]);

  const renderSelect = (
    <div className={styles.Match}>
      <SportDataSelect
        label="Selected match"
        value={matchId}
        options={MATCH_OPTIONS}
        onChange={handleChangeMatch}
      />
    </div>
  );

  if (hockeyStandings.loading || !hockeyStandings.data.items?.length) {
    return (
      <div className={styles.Container}>
        <div className={styles.Header} />
        <div className={styles.Content}>
          <div className={styles.FilterContainer}>{renderSelect}</div>
          {hockeyStandings.loading && (
            <div className={styles.LoadingContainer}>
              <Loader className={styles.Loading} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Container}>
      <div className={styles.Header}>
        <Button className={styles.ImportButton} onClick={handleClickImport}>
          Import
        </Button>
      </div>
      <div className={styles.Content}>
        <div className={styles.FilterContainer}>{renderSelect}</div>
        <Repeater
          id=""
          onChangeRows={() => {}}
          items={repeaterValues}
          isDisabled
          excludedIds={['divider', 'highlight']}
        />
      </div>
    </div>
  );
}
